import { useQueries } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { ProjectAvatar } from '../identity/ProjectAvatar'
import { listPages, type PageInfo } from '../chatv1/transport'
import { relativeAge } from '../ui/relativeTime'
import type { ProjectRow } from '../transport/api'
import { StagePage, type StageChrome } from './StagePage'

/**
 * EVERY PAGE, IN ONE PLACE — owner, 2026-11-03: 在 More 的欄位, 新增 page 的 item, 如同 whiteboard 一般.
 *
 * The pages of every room this person is in (a friend's chat has none), the one written to last first — with its room
 * and when it last changed. Pressing one opens that room on that page. Read with the room's own query (['pages', conv],
 * page/pages.ts usePages), so a page made, renamed or deleted in the room is so here.
 *
 * No red dot, unlike More › Whiteboards: nothing yet tells the service a page has been seen (a board's `markBoardSeen`
 * has no page twin on the web), so every page written by someone else would wear one for ever.
 */
export type PageRowAt = { room: ProjectRow; page: PageInfo; at: number }

/** The rooms that have pages: projects and groups, not a friend's chat. */
export const pageRooms = (projects: ProjectRow[]): ProjectRow[] => projects.filter((p) => !p.dm_peer)

/** `enabled: false` reads only what is already known — More's button counts without asking every room. */
export function useAllPages(rooms: ProjectRow[], enabled = true) {
  const qs = useQueries({
    queries: rooms.map((r) => ({
      queryKey: ['pages', r.project_id],
      queryFn: async () => (await listPages(r.project_id)).pages,
      enabled,
      staleTime: 15_000,
    })),
  })
  const rows: PageRowAt[] = []
  rooms.forEach((room, i) => {
    for (const page of qs[i]!.data ?? []) rows.push({ room, page, at: page.last_ts ?? page.created_ts })
  })
  rows.sort((a, b) => b.at - a.at)
  return { rows, loading: qs.some((q) => q.isLoading), failed: rooms.filter((_, i) => qs[i]!.isError) }
}

export function AllPagesPage({
  rooms,
  onOpenPage,
  ...chrome
}: StageChrome & {
  rooms: ProjectRow[]
  onOpenPage: (room: ProjectRow, page: string) => void
}) {
  const all = useAllPages(rooms)
  return (
    <StagePage
      {...chrome}
      title="Pages"
      backLabel="Back"
      sub={all.rows.length ? `${all.rows.length} in ${new Set(all.rows.map((r) => r.room.project_id)).size} projects` : undefined}
      glyph={
        <span className="lp-chip lp-blue" aria-hidden="true">
          <Icon name="description" size={14} />
        </span>
      }
      className="more-page wb-page"
    >
      {all.loading && !all.rows.length && <p className="more-note">Reading your projects’ pages…</p>}
      {!all.loading && !all.rows.length && (
        <p className="more-note">
          No pages yet. Open a project and press its Pages button — what you, your friends or its agents write there is
          listed here.
        </p>
      )}
      {all.rows.length > 0 && (
        <ul className="wb-list" aria-label="Pages">
          {all.rows.map(({ room, page, at }) => (
            <li key={`${room.project_id}:${page.page}`}>
              <button
                type="button"
                className="sb-row art-row wb-row"
                onClick={() => onOpenPage(room, page.page)}
                title={`Open ${page.name || 'Untitled'} in ${room.name ?? 'the project'}`}
              >
                <ProjectAvatar projectId={room.project_id} label={room.name ?? room.project_id} size={26} />
                <span className="art-row-main">
                  <span className="art-row-name">
                    {page.name || 'Untitled'}
                    {page.edit && <Icon name="lock" size={13} label="Locked" />}
                  </span>
                  <span className="art-row-sub">
                    {room.name ?? 'Project'}
                    {at > 0 ? ` · ${page.last_ts ? 'changed' : 'made'} ${relativeAge((Date.now() - at) / 1000)}` : ''}
                  </span>
                </span>
                <Icon name="chevron_right" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {all.failed.length > 0 && (
        <p className="more-note more-quiet">Could not read the pages of: {all.failed.map((r) => r.name ?? r.project_id).join(', ')}.</p>
      )}
    </StagePage>
  )
}
